import React, { useState } from 'react';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import * as ShopSelectors from 'selectors/ShopSelectors';
import EyeButton from '../../components/EyeButton';
import { useTranslation } from 'services/localization';
import LinkInformationDialog from './LinkInformatioDialog';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useFormContext, Controller } from 'react-hook-form';
import { FormValues } from '../../types';
import { getCustomizerLocaleOnChange } from '../../utils';

const Links = () => {
	const { t } = useTranslation();
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	const { control, watch, setValue } = useFormContext<FormValues>();
	const hidden = watch('onlineLayout.footer.about.hidden');
	const linkObjects = watch('onlineLayout.footer.links.linkObjects');

	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);

	return (
		<Stack spacing={1}>
			<Stack direction="row" justifyContent="space-between">
				<Typography
					variant="body2"
					color={hidden ? 'secondary' : 'default'}
					sx={{ textTransform: 'uppercase', fontWeight: 400 }}
				>
					{t('channels.online.customizer.footer.links.header', 'Links')}
				</Typography>
				<Controller
					name="onlineLayout.footer.links"
					control={control}
					render={({ field }) => (
						<EyeButton
							disabled={!field.value?.heading && !field.value?.linkObjects?.length}
							visible={field.value?.hidden ?? false}
							onChange={(visible) => field.onChange({ ...field.value, hidden: !visible })}
						/>
					)}
				/>
			</Stack>
			<Stack spacing={2}>
				<Controller
					name="onlineLayout.footer.links"
					control={control}
					render={({ field }) => (
						<TextField
							disabled={field.value?.hidden}
							value={field.value?.heading?.def}
							onChange={(event) =>
								field.onChange({
									...field.value,
									heading: getCustomizerLocaleOnChange(
										event.target.value,
										field.value?.heading,
										defaultLanguage,
									),
								})
							}
							label={t('common:form.heading', 'Heading')}
							placeholder={t('common:form.headingPlaceholder', 'Add a heading')}
							InputLabelProps={{
								shrink: true,
							}}
							fullWidth
						/>
					)}
				/>
				<LinkInformationDialog
					onChange={(data) => {
						setValue('onlineLayout.footer.links.linkObjects', [...(linkObjects ?? []), data], {
							shouldDirty: true,
						});
					}}
					isOpen={isDialogOpen}
					onClose={() => setIsDialogOpen(false)}
				/>
				<Stack>
					<Controller
						name="onlineLayout.footer.links.linkObjects"
						control={control}
						render={({ field }) => (
							<>
								{(field.value ?? []).map((link, i) => (
									<Stack key={i} alignItems="center" justifyContent="space-between" direction="row">
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											{link.label?.def}
										</Typography>
										<IconButton
											onClick={() => {
												const newLinks = field.value?.filter((_, idx) => i !== idx);
												field.onChange(newLinks);
											}}
										>
											<RiDeleteBinLine color="secondary" size={16} />
										</IconButton>
									</Stack>
								))}
							</>
						)}
					/>
				</Stack>
				<Stack alignItems="flex-start">
					<Button variant="text" onClick={() => setIsDialogOpen(true)}>
						{`+ ${t('channels.online.customizer.footer.links.dialog.header', 'Add new link')}`}
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default Links;
